import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownProps {
  /** Options to display in the dropdown */
  options: DropdownOption[];
  /** Current selected value */
  value: string;
  /** Called when selection changes */
  onChange: (value: string) => void;
  /** Label shown above the trigger */
  label?: string;
  /** Placeholder when no value selected */
  placeholder?: string;
  /** Additional class names for the trigger button */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** ID for the trigger element (for form labels) */
  id?: string;
}

/**
 * Dropdown - Custom select-style dropdown matching app theme.
 * Use for Contract term, Billing cycle, and similar form fields.
 */
export function Dropdown({
  options,
  value,
  onChange,
  label,
  placeholder = 'Select...',
  className = '',
  disabled = false,
  id,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-[#EBFFEE] mb-2">
          {label}
        </label>
      )}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between px-4 py-3 rounded-lg
          bg-[#504A4A] text-[#EBFFEE] border-none outline-none
          focus:ring-2 focus:ring-[#9B1421]/50
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
          ${isOpen ? 'ring-2 ring-[#9B1421]/50' : ''}
          ${className}
        `}
      >
        <span className={selectedOption ? 'text-[#EBFFEE]' : 'text-[#A9A3A3]'}>
          {displayLabel}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-[#A9A3A3] transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute top-full left-0 right-0 mt-2 bg-[#221F1F] border border-[#504A4A] rounded-lg shadow-lg z-50 py-2 max-h-60 overflow-y-auto scrollbar-styled"
            role="listbox"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={value === option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                  value === option.value
                    ? 'bg-[#9B1421]/20 text-[#EBFFEE]'
                    : 'text-[#EBFFEE] hover:bg-white/5'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
